import React, { useState } from "react";
import { APIProvider, AdvancedMarker, Map } from "@vis.gl/react-google-maps";
import { Box, Button, TextField, Typography } from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import { clearFromStorage, saveToStorage } from "@/app/utils/localStorageHelper/checkoutStorage";
import {
  EagleViewAddressPrefill,
  STORAGE_KEYS,
} from "@/app/utils/localStorageHelper/checkoutKeys";

interface LatLng {
  lat: number;
  lng: number;
}

interface ParsedAddress {
  address1: string;
  city: string;
  stateName: string;
  zip: string;
}

const DEFAULT_CENTER: LatLng = { lat: 39.5, lng: -98.35 };

const EagleViewMapSection: React.FC = () => {
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "";

  const [searchInput, setSearchInput] = useState("");
  const [markerPosition, setMarkerPosition] = useState<LatLng | null>(null);
  const [mapCenter, setMapCenter] = useState<LatLng>(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(4);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [selectedAddress, setSelectedAddress] =
    useState<ParsedAddress | null>(null);
  const [addressSaved, setAddressSaved] = useState(false);

  const parseAddressComponents = (
    components: google.maps.GeocoderAddressComponent[]
  ): ParsedAddress => {
    const get = (
      type: string,
      nameType: "long_name" | "short_name" = "long_name"
    ) => components.find((c) => c.types.includes(type))?.[nameType] ?? "";

    return {
      address1: `${get("street_number")} ${get("route")}`.trim(),
      city:
        get("locality") ||
        get("sublocality") ||
        get("administrative_area_level_2"),
      stateName: get("administrative_area_level_1"),
      zip: get("postal_code"),
    };
  };

  const saveAddressToCheckout = (addr: ParsedAddress) => {
    if (!userId) return;
    const payload: EagleViewAddressPrefill = {
      address1: addr.address1,
      city: addr.city,
      stateName: addr.stateName,
      zip: addr.zip,
    };
    saveToStorage(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL, payload);
    setAddressSaved(true);
  };

  const findStreetLevelResult = (
    results: google.maps.GeocoderResult[]
  ): google.maps.GeocoderResult | null =>
    results.find((r) =>
      r.types.some((t) =>
        ["street_address", "premise", "subpremise"].includes(t)
      )
    ) ?? null;

  // Used by search and current location — re-centers the map
  const applySearchResult = (
    result: google.maps.GeocoderResult,
    pos: LatLng
  ) => {
    const parsed = parseAddressComponents(result.address_components);
    setMarkerPosition(pos);
    setMapCenter(pos);
    setZoom(17);
    setSelectedAddress(parsed);
    if (parsed.address1 && parsed.zip) {
      setValidationMessage(null);
      saveAddressToCheckout(parsed);
    } else {
      setAddressSaved(false);
      setValidationMessage(
        "Please enter a full street address (including street number and zip code) to pre-fill checkout."
      );
    }
  };

  // Used by map click — keeps the current map view, only moves the marker
  const applyMapClickResult = (
    result: google.maps.GeocoderResult,
    pos: LatLng
  ) => {
    const parsed = parseAddressComponents(result.address_components);
    setMarkerPosition(pos);
    setSelectedAddress(parsed);
    if (parsed.address1 && parsed.zip) {
      setValidationMessage(null);
      saveAddressToCheckout(parsed);
    } else {
      setAddressSaved(false);
      setValidationMessage(
        "Selected location does not have a specific street address. Please click on a building or use the search box."
      );
    }
  };

  const geocodeAddress = (address: string) => {
    if (!address.trim()) {
      setValidationMessage("Please enter an address to search.");
      return;
    }
    setValidationMessage(null);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        const best = findStreetLevelResult(results) ?? results[0];
        const location = best.geometry.location;
        applySearchResult(best, {
          lat: location.lat(),
          lng: location.lng(),
        });
      } else {
        setValidationMessage("Address not found. Please try a different search.");
      }
    });
  };

  const handleMapClick = (lat: number, lng: number) => {
    const pos: LatLng = { lat, lng };
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: pos }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        const best = findStreetLevelResult(results) ?? results[0];
        applyMapClickResult(best, pos);
      } else {
        setMarkerPosition(pos);
        setSelectedAddress(null);
        setValidationMessage("Could not resolve an address for this location.");
      }
    });
  };

  const handleSearch = () => geocodeAddress(searchInput);

  const handleClear = () => {
    setSearchInput("");
    setMarkerPosition(null);
    setMapCenter(DEFAULT_CENTER);
    setZoom(4);
    setValidationMessage(null);
    setSelectedAddress(null);
    setAddressSaved(false);
    clearFromStorage(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") geocodeAddress(searchInput);
  };

  const handleUseCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const position: LatLng = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: position }, (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            const best = findStreetLevelResult(results) ?? results[0];
            applySearchResult(best, position);
          } else {
            setMarkerPosition(position);
            setMapCenter(position);
            setZoom(17);
            setValidationMessage(null);
          }
        });
      },
      () => {
        setValidationMessage("Unable to retrieve your location.");
      }
    );
  };

  return (
    <Box sx={{ mt: 3, mb: 6, width: "100%" }}>
      <Typography variant="h6" gutterBottom>
        Enter Address
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        <TextField
          size="small"
          variant="outlined"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter address, city, state, or zip"
          autoComplete="off"
          sx={{ flexGrow: 1, minWidth: 200 }}
        />
        <Button
          className="blueButtonOutline"
          variant="outlined"
          onClick={handleSearch}
        >
          Search
        </Button>
        <Button
          className="blueButtonOutline"
          variant="outlined"
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
      <Button
        size="small"
        variant="text"
        className="mapUseCurrentLocation"
        onClick={handleUseCurrentLocation}
        sx={{ mb: 1, fontSize: "75%" }}
      >
        <MyLocationIcon fontSize="small" sx={{ mr: 1 }} />
        Use My Current Location
      </Button>
      {validationMessage && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {validationMessage}
        </Typography>
      )}
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""}>
        <Box sx={{ height: 350, width: "100%" }}>
          <Map
            key={`eagleview-map-${mapCenter.lat}-${mapCenter.lng}-${zoom}`}
            mapId="EAGLEVIEW_MAP"
            defaultZoom={zoom}
            defaultCenter={mapCenter}
            mapTypeId="satellite"
            renderingType="UNINITIALIZED"
            style={{ width: "100%", height: "100%" }}
            onClick={(e) => {
              if (e.detail.latLng) {
                handleMapClick(e.detail.latLng.lat, e.detail.latLng.lng);
              }
            }}
          >
            {markerPosition && <AdvancedMarker position={markerPosition} />}
          </Map>
        </Box>
      </APIProvider>
      {selectedAddress && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Selected address:
          </Typography>
          <Typography variant="body2">
            {[
              selectedAddress.address1,
              selectedAddress.city,
              selectedAddress.stateName,
              selectedAddress.zip,
            ]
              .filter(Boolean)
              .join(", ")}
          </Typography>
          {addressSaved && userId && (
            <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
              Address saved — it will be pre-filled at checkout.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EagleViewMapSection;
